import { messageService } from '@services/message.service';
import { message } from 'antd';
import { put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import {
  loadStreamMessages,
  loadStreamMessagesSuccess,
  loadMoreStreamMessages,
  loadMoreStreamMessagesSuccess,
  sendStreamMessage,
  sendStreamMessageSuccess,
  sendStreamMessageFail,
  getStreamConversationSuccess
} from './streamChatSlice';

function* loadStreamMessagesSaga(action: PayloadAction<{ conversationId: string; limit: number }>) {
  try {
    const { conversationId, limit } = action.payload;
    const resp = yield messageService.getPublicMessages(conversationId, {
      limit,
      sort: 'createdAt',
      sortType: 'desc'
    });
    // resp is already unwrapped (no .data needed)
    yield put(loadStreamMessagesSuccess({
      conversationId,
      data: resp.data,
      total: resp.total
    }));
  } catch (e) {
    const error = yield Promise.resolve(e);
    message.error(error?.message || 'An error occurred loading messages');
  }
}

function* loadMoreStreamMessagesSaga(action: PayloadAction<{ conversationId: string; limit: number; offset: number }>) {
  try {
    const { conversationId, limit, offset } = action.payload;
    const resp = yield messageService.getPublicMessages(conversationId, {
      limit,
      offset,
      sort: 'createdAt',
      sortType: 'desc'
    });
    // resp is already unwrapped (no .data needed)
    yield put(loadMoreStreamMessagesSuccess({
      conversationId,
      data: resp.data,
      total: resp.total
    }));
  } catch (e) {
    const error = yield Promise.resolve(e);
    message.error(error?.message || 'An error occurred loading more messages');
  }
}

function* sendStreamMessageSaga(action: PayloadAction<any>) {
  try {
    const { conversationId, data, ...rest } = action.payload;
    // Flatten the data object to match backend expectations
    const resp = yield messageService.sendStreamMessage(conversationId, { ...data, ...rest });
    // resp format: { status: 0, data: {...message} }
    yield put(sendStreamMessageSuccess({
      conversationId,
      message: resp.data
    }));
  } catch (e) {
    const error = yield Promise.resolve(e);
    yield put(sendStreamMessageFail(error));
    message.error(error?.message || 'An error occurred sending message');
  }
}

function* getStreamConversationSaga(action: PayloadAction<string>) {
  try {
    const resp = yield messageService.findPublicConversationPerformer(action.payload);
    // resp is already unwrapped (no .data needed)
    yield put(getStreamConversationSuccess(resp));
  } catch (e) {
    const error = yield Promise.resolve(e);
    message.error(error?.message || 'An error occurred getting conversation');
  }
}

export default function* streamChatSaga() {
  yield takeLatest(loadStreamMessages.type, loadStreamMessagesSaga);
  yield takeLatest(loadMoreStreamMessages.type, loadMoreStreamMessagesSaga);
  yield takeLatest(sendStreamMessage.type, sendStreamMessageSaga);
  yield takeLatest('GET_STREAM_CONVERSATION', getStreamConversationSaga);
}